// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {(() => void) | null} */
  #onFormInput = null;

  connectedCallback() {
    super.connectedCallback();
    // Guard the dynamic "Buy it now" button, which bypasses the form's
    // native required-field validation by submitting straight to checkout.
    this.#setupCheckoutGuard();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#form && this.#onFormInput) {
      this.#form.removeEventListener('input', this.#onFormInput);
      this.#form.removeEventListener('change', this.#onFormInput);
    }
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  /**
   * Handles selection of a file in an image-upload slot: shows a preview and
   * filename, and validates the file size against the per-input limit. An
   * oversized file is marked invalid via setCustomValidity so it blocks both
   * the add-to-cart button and the accelerated-checkout guard.
   * @param {Event} event
   */
  handleFileChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const slot = input.closest('[data-image-slot]');
    if (!slot) return;

    const errorEl = slot.querySelector('[data-image-error]');
    const file = input.files?.[0];

    // Reset previous state.
    input.setCustomValidity('');
    if (errorEl) errorEl.textContent = '';
    slot.classList.remove('has-error');

    if (!file) {
      this.#clearSlotPreview(slot);
      return;
    }

    const maxBytes = Number(input.dataset.maxBytes) || 0;
    if (maxBytes && file.size > maxBytes) {
      const message = input.dataset.sizeError || 'File is too large.';
      input.setCustomValidity(message);
      if (errorEl) errorEl.textContent = message;
      slot.classList.add('has-error');
    }

    const filenameEl = slot.querySelector('[data-image-filename]');
    const meta = /** @type {HTMLElement | null} */ (slot.querySelector('[data-image-meta]'));
    const labelText = /** @type {HTMLElement | null} */ (slot.querySelector('[data-image-label]'));
    const preview = /** @type {HTMLImageElement | null} */ (slot.querySelector('[data-image-preview]'));

    if (filenameEl) filenameEl.textContent = file.name;
    if (meta) meta.hidden = false;
    if (labelText) labelText.hidden = true;
    slot.classList.add('has-file');

    if (preview && file.type.startsWith('image/')) {
      const previousUrl = preview.dataset.objectUrl;
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      const url = URL.createObjectURL(file);
      preview.dataset.objectUrl = url;
      preview.src = url;
      preview.hidden = false;
    }
  }

  /**
   * Clears the file from an image-upload slot when the Remove button is used.
   * @param {Event} event
   */
  handleFileRemove(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slot = target.closest('[data-image-slot]');
    if (!slot) return;

    const input = /** @type {HTMLInputElement | null} */ (slot.querySelector('input[type="file"]'));
    if (input) {
      input.value = '';
      input.setCustomValidity('');
    }
    this.#clearSlotPreview(slot);

    // Nudge the checkout guard to re-evaluate now that a file was removed.
    input?.form?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Resets the visual state of an image-upload slot.
   * @param {Element} slot
   */
  #clearSlotPreview(slot) {
    const preview = /** @type {HTMLImageElement | null} */ (slot.querySelector('[data-image-preview]'));
    const meta = /** @type {HTMLElement | null} */ (slot.querySelector('[data-image-meta]'));
    const labelText = /** @type {HTMLElement | null} */ (slot.querySelector('[data-image-label]'));
    const errorEl = slot.querySelector('[data-image-error]');

    if (preview) {
      const previousUrl = preview.dataset.objectUrl;
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      delete preview.dataset.objectUrl;
      preview.removeAttribute('src');
      preview.hidden = true;
    }
    if (meta) meta.hidden = true;
    if (labelText) labelText.hidden = false;
    if (errorEl) errorEl.textContent = '';
    slot.classList.remove('has-file', 'has-error');
  }

  /**
   * Finds the associated product form and wires up an overlay over the
   * accelerated-checkout button so it can't be used until required custom
   * properties are filled in.
   */
  #setupCheckoutGuard() {
    // Text blocks expose a `textInput` ref; image blocks have file inputs only.
    // Either way the field's `.form` resolves the form via its `form` attribute.
    const input = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (
      this.refs.textInput ?? this.querySelector('input, textarea, select')
    );
    const form = input?.form ?? this.closest('form');
    if (!form) return;
    this.#form = form;

    const acceleratedContainer = /** @type {HTMLElement | null} */ (form.querySelector('.accelerated-checkout-block'));
    if (!acceleratedContainer) return;

    // Only one overlay per form, even when several custom-property blocks exist.
    let overlay = /** @type {HTMLElement | null} */ (acceleratedContainer.querySelector('.custom-property__checkout-guard'));
    if (!overlay) {
      acceleratedContainer.style.position = 'relative';
      overlay = document.createElement('div');
      overlay.className = 'custom-property__checkout-guard';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Surface the native validation message and jump to the first empty field.
        form.reportValidity();
        const firstInvalid = /** @type {HTMLElement | null} */ (
          Array.from(form.elements).find(
            (el) => el instanceof HTMLElement && 'checkValidity' in el && !(/** @type {any} */ (el).checkValidity())
          ) ?? null
        );
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid?.focus({ preventScroll: true });
      });
      acceleratedContainer.appendChild(overlay);
    }

    const update = () => {
      const valid = Array.from(form.elements).every(
        (el) => !(el instanceof HTMLElement) || !('checkValidity' in el) || /** @type {any} */ (el).checkValidity()
      );
      acceleratedContainer.classList.toggle('custom-property__checkout-blocked', !valid);
    };

    this.#onFormInput = update;
    form.addEventListener('input', update);
    form.addEventListener('change', update);
    update();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    // Only reveal the counter once the customer reaches the limit.
    this.classList.toggle('at-max', maxLength > 0 && currentLength >= maxLength);

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
